import { Box, BoxProps, Spacer } from 'stylewhere/components'
import { Switch as ChakraSwitch } from '@chakra-ui/react'
import styled from '@emotion/styled'
import { ReactNode, useState, useEffect } from 'react'

interface Props {
  name: string
  id?: string
  label: string | ReactNode
  labelPosition?: 'left' | 'right'
  check?: boolean
  onChange: (checked: boolean) => void
  wrapperProps?: BoxProps
  disabled?: boolean
  boxed?: boolean
}

export const Switch: React.FC<Props> = ({
  name,
  id,
  label,
  labelPosition = 'right',
  check,
  onChange,
  wrapperProps,
  boxed = false,
  disabled = false
}) => {
  const [checked, setChecked] = useState(check ?? false)

  useEffect(() => {
    if (check !== undefined) {
      setChecked(check)
    }
  }, [check])

  const handleSwitchChange = (details: { checked: boolean }) => {
    if (!disabled) {
      const newChecked = details.checked
      setChecked(newChecked)
      onChange(newChecked)
    }
  }

  const CSwitch = (
    <StyledSwitchRoot checked={checked} onCheckedChange={handleSwitchChange} disabled={disabled}>
      <ChakraSwitch.HiddenInput />
      <ChakraSwitch.Control>
        <ChakraSwitch.Thumb />
      </ChakraSwitch.Control>
    </StyledSwitchRoot>
  )

  const Label = (
    <LabelText
      data-testid={`switch-label`}
      htmlFor={name}
      onClick={() => {
        if (!disabled) {
          const newChecked = !checked
          setChecked(newChecked)
          onChange(newChecked)
        }
      }}
      style={{
        cursor: disabled ? 'not-allowed' : 'pointer',
        flex: 1,
        color: disabled ? '#eeeeee' : '#666666'
      }}
    >
      {label}
    </LabelText>
  )

  return (
    <SwitchBox data-testid={`switch`} boxed={boxed} row vcenter {...wrapperProps}>
      {labelPosition === 'left' && (
        <>
          {Label}
          {CSwitch}
        </>
      )}
      {labelPosition === 'right' && (
        <>
          {CSwitch}
          <Spacer />
          {Label}
        </>
      )}
    </SwitchBox>
  )
}

const SwitchBox = styled(Box)<{ boxed: boolean }>`
  ${({ boxed }) =>
    boxed &&
    `
      background: #FBFBFB;
      border: 2px solid #DCDCDC;
      width: fit-content;
      padding: 16px 12px;  
      border-radius: 10px;
      font-size: 16px;
    `}
`

const LabelText = styled.label`
  user-select: none;
`

const StyledSwitchRoot = styled(ChakraSwitch.Root)`
  [data-part='control'] {
    background-color: #dcdcdc !important;
  }

  &[data-checked='true'] [data-part='control'],
  &[data-state='checked'] [data-part='control'] {
    background-color: #3182ce !important;
  }
`
